import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "";

const DEFAULT_POLARITY_OPTIONS = ["positive", "negative", "mixed"];
const DEFAULT_TOPIC_GROUPS = [
  "room",
  "service",
  "facilities",
  "food",
  "access",
  "security",
  "value",
  "location",
];

const FIELD_GUIDE = [
  {
    key: "id",
    title: "id",
    storage: "Auto-generated primary key",
    purpose:
      "Identificador interno de base de datos para enlazar el topic con reviews detectadas, action plans, constraints y agregados.",
    howToFill: "No se alimenta manualmente. Pilcore lo genera al guardar.",
    warning:
      "No debe usarse como etiqueta operativa ni tocarse para reordenar el catálogo.",
    example: "Ejemplo: `id = 14` puede existir en BBDD, pero el negocio debe operar con `code` y `label`.",
  },
  {
    key: "code",
    title: "code",
    storage: "Stable machine key",
    purpose:
      "Es la identidad técnica persistente del topic. Reglas, IA, métricas y futuras integraciones lo usarán como referencia estable.",
    howToFill:
      "Usa minúsculas, sin espacios y con underscores si hace falta. Debe ser corto, semántico y permanecer estable en el tiempo.",
    warning:
      "No renombres el `code` a la ligera cuando ya existan detecciones o action plans asociados.",
    example: "Ejemplos recomendados: `cleanliness`, `noise`, `small_rooms`, `stairs_access`.",
  },
  {
    key: "label",
    title: "label",
    storage: "Human-readable label",
    purpose:
      "Texto visible para usuarios de negocio y para administración interna del producto.",
    howToFill:
      "Escribe una etiqueta clara y corta que cualquier equipo hotelero entienda sin contexto técnico.",
    warning:
      "Debe ser legible, pero no sustituye al `code` como identificador estable.",
    example: "Ejemplos: `Cleanliness`, `Noise`, `Small rooms`, `Stairs access`.",
  },
  {
    key: "description",
    title: "description",
    storage: "Detailed semantic scope",
    purpose:
      "Aclara qué entra y qué no entra en el topic para alinear producto, reglas e interpretación humana.",
    howToFill:
      "Describe el alcance real del topic y los casos típicos que deberían mapearse aquí.",
    warning:
      "No la dejes vacía si el topic puede ser ambiguo o solaparse con otros.",
    example:
      "Ejemplo: `Complaints about external or internal noise during the stay, especially at night.`",
  },
  {
    key: "topic_group",
    title: "topic_group",
    storage: "Strategic grouping",
    purpose:
      "Agrupa topics por gran bloque analítico para dashboards, filtros y agregados estratégicos.",
    howToFill:
      "Usa una familia consistente como `room`, `service`, `facilities`, `food`, `access`, `security` o `value`.",
    warning:
      "No mezcles grupo táctico con severidad. `topic_group` clasifica el área del problema, no lo grave que es.",
    example: "Ejemplos: `room` para `small_rooms`; `food` para `bad_breakfast`; `access` para `stairs_access`.",
  },
  {
    key: "polarity_default",
    title: "polarity_default",
    storage: "Expected baseline polarity",
    purpose:
      "Indica si el topic suele representar menciones positivas, negativas o mixtas por defecto.",
    howToFill:
      "Selecciona la polaridad que mejor represente la interpretación habitual del topic en analítica.",
    warning:
      "No implica que todas las menciones del topic vayan a tener esa polaridad. Es solo una referencia base del catálogo.",
    example:
      "Ejemplo: `cleanliness` puede ser `mixed`; `noise` suele ser `negative`; `location` puede ser `positive` o `mixed` según criterio de producto.",
  },
  {
    key: "is_active",
    title: "is_active",
    storage: "Operational availability flag",
    purpose:
      "Permite retirar un topic del uso activo sin perder historia ni romper relaciones existentes.",
    howToFill:
      "Déjalo activo mientras el topic forme parte del modelo analítico actual. Desactívalo si se reemplaza o se depreca.",
    warning:
      "Si ya existen review_topics, action plans o aggregates asociados, desactivar es más seguro que borrar.",
    example:
      "Uso típico: un topic histórico demasiado genérico puede quedar inactivo cuando producto lo sustituye por topics más precisos.",
  },
  {
    key: "created_updated_at",
    title: "created_at / updated_at",
    storage: "Audit timestamps",
    purpose:
      "Sirven para auditar cuándo nació el topic y cuándo se tocó por última vez.",
    howToFill: "No se alimentan manualmente. Pilcore los rellena automáticamente.",
    warning:
      "Úsalos para trazabilidad interna, no como configuración funcional del catálogo.",
    example: "Ayuda a revisar cuándo se introdujo un topic nuevo o cuándo se ajustó su taxonomía.",
  },
];

const EXAMPLE_TOPICS = [
  {
    code: "cleanliness",
    label: "Cleanliness",
    topic_group: "room",
    polarity_default: "mixed",
    is_active: true,
  },
  {
    code: "noise",
    label: "Noise",
    topic_group: "room",
    polarity_default: "negative",
    is_active: true,
  },
  {
    code: "small_rooms",
    label: "Small rooms",
    topic_group: "room",
    polarity_default: "negative",
    is_active: true,
  },
  {
    code: "bad_breakfast",
    label: "Bad breakfast",
    topic_group: "food",
    polarity_default: "negative",
    is_active: true,
  },
  {
    code: "staff_kindness",
    label: "Staff kindness",
    topic_group: "service",
    polarity_default: "positive",
    is_active: true,
  },
  {
    code: "stairs_access",
    label: "Stairs access",
    topic_group: "access",
    polarity_default: "negative",
    is_active: true,
  },
];

const defaultFormState = {
  id: null,
  code: "",
  label: "",
  description: "",
  topic_group: "room",
  polarity_default: "mixed",
  is_active: true,
  review_topics_count: 0,
  action_plans_count: 0,
  operational_constraints_count: 0,
  daily_aggregates_count: 0,
  can_be_deleted: true,
  created_at: null,
  updated_at: null,
};

function normalizeCode(rawValue) {
  return String(rawValue || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function humanizeToken(value) {
  if (!value) return "Not set";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatTimestamp(value) {
  if (!value) return "Not available yet";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString();
}

const FIELD_GUIDE_BY_KEY = FIELD_GUIDE.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

function getAuthConfig() {
  const token = localStorage.getItem("palantir_token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

function getErrorMessage(error, fallbackMessage) {
  return error?.response?.data?.error || fallbackMessage;
}

function ReviewTopicsCatalogAdmin() {
  const [topics, setTopics] = useState([]);
  const [polarityOptions, setPolarityOptions] = useState(DEFAULT_POLARITY_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [polarityFilter, setPolarityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFormModal, setShowFormModal] = useState(false);
  const [formState, setFormState] = useState(defaultFormState);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchTopics = async ({ keepCurrentLoading = false } = {}) => {
    if (!keepCurrentLoading) setLoading(true);

    try {
      const response = await axios.get(
        `${API_BASE}/api/admin/review-topics-catalog`,
        getAuthConfig()
      );
      setTopics(Array.isArray(response.data?.items) ? response.data.items : []);
      setPolarityOptions(
        Array.isArray(response.data?.polarity_options) && response.data.polarity_options.length
          ? response.data.polarity_options
          : DEFAULT_POLARITY_OPTIONS
      );
      setLoadError("");
    } catch (error) {
      setLoadError(getErrorMessage(error, "No se pudo cargar el catálogo de topics."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  const topicGroupOptions = useMemo(() => {
    const values = new Set(DEFAULT_TOPIC_GROUPS);
    topics.forEach((topic) => {
      if (topic.topic_group) values.add(topic.topic_group);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [topics]);

  const filteredTopics = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return topics.filter((topic) => {
      if (groupFilter !== "all" && topic.topic_group !== groupFilter) return false;
      if (polarityFilter !== "all" && (topic.polarity_default || "") !== polarityFilter) return false;
      if (statusFilter === "active" && !topic.is_active) return false;
      if (statusFilter === "inactive" && topic.is_active) return false;

      if (!query) return true;

      return [
        topic.id,
        topic.code,
        topic.label,
        topic.description,
        topic.topic_group,
        topic.topic_group_label,
        topic.polarity_default,
        topic.polarity_default_label,
        topic.is_active ? "active" : "inactive",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [groupFilter, polarityFilter, searchQuery, statusFilter, topics]);

  const metrics = useMemo(() => {
    const active = topics.filter((item) => item.is_active).length;
    const negativeDefault = topics.filter((item) => item.polarity_default === "negative").length;
    const mixedDefault = topics.filter((item) => item.polarity_default === "mixed").length;
    const withHistory = topics.filter(
      (item) =>
        Number(item.review_topics_count || 0) > 0 ||
        Number(item.action_plans_count || 0) > 0 ||
        Number(item.operational_constraints_count || 0) > 0 ||
        Number(item.daily_aggregates_count || 0) > 0
    ).length;

    return {
      total: topics.length,
      active,
      inactive: topics.length - active,
      groups: topicGroupOptions.length,
      negativeDefault,
      mixedDefault,
      withHistory,
      filtered: filteredTopics.length,
    };
  }, [filteredTopics.length, topicGroupOptions.length, topics]);

  const isEditing = Boolean(formState.id);

  const openCreateModal = () => {
    setFormState({
      ...defaultFormState,
      topic_group: topicGroupOptions.includes(defaultFormState.topic_group)
        ? defaultFormState.topic_group
        : topicGroupOptions[0] || defaultFormState.topic_group,
      polarity_default: polarityOptions.includes(defaultFormState.polarity_default)
        ? defaultFormState.polarity_default
        : polarityOptions[0] || defaultFormState.polarity_default,
    });
    setFormError("");
    setShowFormModal(true);
  };

  const openEditModal = (topic) => {
    setFormState({
      ...defaultFormState,
      ...topic,
      code: String(topic.code || ""),
      label: String(topic.label || ""),
      description: String(topic.description || ""),
      topic_group: topic.topic_group || defaultFormState.topic_group,
      polarity_default: topic.polarity_default || defaultFormState.polarity_default,
      is_active: Boolean(topic.is_active),
    });
    setFormError("");
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setShowFormModal(false);
    setFormError("");
  };

  const openDeleteModal = (topic) => {
    setDeleteTarget(topic);
    setDeleteError("");
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError("");

    const payload = {
      code: normalizeCode(formState.code),
      label: String(formState.label || "").trim(),
      description: String(formState.description || "").trim(),
      topic_group: normalizeCode(formState.topic_group),
      polarity_default: formState.polarity_default || null,
      is_active: Boolean(formState.is_active),
    };

    try {
      if (isEditing) {
        await axios.put(
          `${API_BASE}/api/admin/review-topics-catalog/${formState.id}`,
          payload,
          getAuthConfig()
        );
        setFeedback("Topic actualizado correctamente.");
      } else {
        await axios.post(`${API_BASE}/api/admin/review-topics-catalog`, payload, getAuthConfig());
        setFeedback("Topic creado correctamente.");
      }

      setShowFormModal(false);
      await fetchTopics({ keepCurrentLoading: true });
    } catch (error) {
      setFormError(getErrorMessage(error, "No se pudo guardar el topic."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event) => {
    event.preventDefault();
    if (!deleteTarget?.id) return;

    setDeleting(true);
    setDeleteError("");

    try {
      await axios.delete(
        `${API_BASE}/api/admin/review-topics-catalog/${deleteTarget.id}`,
        getAuthConfig()
      );
      setFeedback("Topic eliminado correctamente.");
      setDeleteTarget(null);
      await fetchTopics({ keepCurrentLoading: true });
    } catch (error) {
      setDeleteError(
        getErrorMessage(error, "No se pudo eliminar el topic. Si tiene historia, desactívalo.")
      );
    } finally {
      setDeleting(false);
    }
  };

  const uiCss = `
    .rt-page{width:100%;max-width:100%;margin:0 auto;padding:0 18px 16px;height:100%;min-height:0;box-sizing:border-box;overflow:auto}
    .rt-stack{display:flex;flex-direction:column;gap:16px;min-height:100%}
    .rt-card{position:relative;border-radius:18px;border:1px solid transparent;background:linear-gradient(180deg,rgba(25,25,51,.94),rgba(10,10,22,.94)) padding-box,linear-gradient(135deg,rgba(48,216,255,.55),rgba(168,85,247,.65),rgba(255,77,79,.45)) border-box;box-shadow:0 28px 90px rgba(0,0,0,.58);overflow:hidden}
    .rt-card:before{content:"";position:absolute;inset:0;background:radial-gradient(900px 300px at 10% 0%,rgba(48,216,255,.14),transparent 60%),radial-gradient(700px 260px at 90% 10%,rgba(168,85,247,.16),transparent 62%),radial-gradient(800px 260px at 40% 110%,rgba(255,77,79,.10),transparent 60%);pointer-events:none;opacity:.95}
    .rt-card>*{position:relative;z-index:1}
    .rt-cardHeader{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;padding:18px 18px 14px;border-bottom:1px solid rgba(255,255,255,.08)}
    .rt-title{margin:0;font-size:1.35rem;font-weight:800;color:transparent;background:linear-gradient(90deg,#e6e6f7 0%,rgba(48,216,255,.95) 40%,rgba(168,85,247,.95) 85%);-webkit-background-clip:text;background-clip:text}
    .rt-subtitle{margin-top:4px;font-size:.88rem;color:rgba(230,230,247,.72);line-height:1.58;max-width:940px}
    .rt-toolbar{display:flex;justify-content:flex-end;align-items:center;gap:10px;flex-wrap:wrap}
    .rt-body{padding:18px}
    .rt-banner{margin:14px 18px 0;padding:12px 14px;border-radius:14px;font-size:.9rem;line-height:1.55}
    .rt-banner--success{border:1px solid rgba(34,197,94,.32);background:rgba(34,197,94,.12);color:rgba(226,255,236,.92)}
    .rt-banner--error{border:1px solid rgba(255,77,79,.35);background:rgba(255,77,79,.12);color:rgba(255,228,230,.92)}
    .rt-metricsGrid,.rt-guideGrid,.rt-twoCol,.rt-checkGrid{display:grid;gap:12px}
    .rt-metricsGrid{grid-template-columns:repeat(5,minmax(0,1fr))}
    .rt-twoCol{grid-template-columns:minmax(0,1.2fr) minmax(300px,.8fr)}
    .rt-guideGrid{grid-template-columns:repeat(2,minmax(0,1fr))}
    .rt-checkGrid{grid-template-columns:repeat(2,minmax(0,1fr))}
    .rt-metricCard,.rt-guideCard,.rt-helperItem,.rt-field,.rt-checkCard{border-radius:16px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);padding:16px}
    .rt-metricLabel,.rt-filterLabel,.rt-guideMeta{font-size:.76rem;text-transform:uppercase;letter-spacing:.09em;color:rgba(230,230,247,.54)}
    .rt-metricValue{margin-top:10px;font-size:2rem;font-weight:800;color:rgba(230,230,247,.96);line-height:1}
    .rt-metricHelp,.rt-guideText,.rt-helperItem,.rt-fieldHelp,.rt-checkHelp,.rt-tableMeta{margin-top:10px;font-size:.84rem;color:rgba(230,230,247,.72);line-height:1.6}
    .rt-filters{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:14px}
    .rt-filterGroup{flex:1 1 200px;min-width:170px}
    .rt-input,.rt-select,.rt-textarea{width:100%;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.10);color:rgba(230,230,247,.92);border-radius:12px;padding:10px 12px;outline:none}
    .rt-input:focus,.rt-select:focus,.rt-textarea:focus{background:rgba(255,255,255,.06);border-color:rgba(48,216,255,.55);box-shadow:0 0 0 4px rgba(48,216,255,.12)}
    .rt-input::placeholder,.rt-textarea::placeholder{color:rgba(230,230,247,.45)}
    .rt-btn{border-radius:12px;padding:10px 14px;font-size:.93rem;font-weight:700;border:1px solid transparent;cursor:pointer;transition:transform .12s ease;background:rgba(255,255,255,.05);color:rgba(230,230,247,.92)}
    .rt-btn:disabled{opacity:.55;cursor:not-allowed;transform:none}
    .rt-btn:hover:not(:disabled){transform:translateY(-1px)}
    .rt-btn--primary{border-color:rgba(48,216,255,.55);background:linear-gradient(135deg,rgba(48,216,255,.18),rgba(168,85,247,.30))}
    .rt-btn--ghost{border-color:rgba(255,255,255,.16)}
    .rt-btn--danger{border-color:rgba(255,77,79,.6);background:linear-gradient(135deg,rgba(255,77,79,.14),rgba(168,85,247,.22))}
    .rt-tableWrap,.rt-exampleWrap{overflow:auto;border-radius:16px;border:1px solid rgba(255,255,255,.08);background:rgba(6,6,14,.38)}
    .rt-table,.rt-exampleTable{width:100%;border-collapse:separate;border-spacing:0}
    .rt-table{min-width:1040px}
    .rt-exampleTable{min-width:720px}
    .rt-table thead th,.rt-exampleTable th{position:sticky;top:0;z-index:2;padding:14px;text-align:left;font-size:.78rem;text-transform:uppercase;letter-spacing:.09em;color:rgba(230,230,247,.64);background:linear-gradient(180deg,rgba(22,22,50,.96),rgba(12,12,28,.96));border-bottom:1px solid rgba(255,255,255,.08)}
    .rt-table tbody td,.rt-exampleTable td{padding:14px;border-bottom:1px solid rgba(255,255,255,.06);vertical-align:top;color:rgba(230,230,247,.88);font-size:.92rem}
    .rt-table tbody tr:hover td,.rt-exampleTable tbody tr:hover td{background:rgba(255,255,255,.025)}
    .rt-pill{display:inline-flex;align-items:center;gap:6px;padding:5px 10px;border-radius:999px;font-size:.76rem;font-weight:700;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.05);color:rgba(230,230,247,.92)}
    .rt-pill--id{color:rgba(48,216,255,.94);border-color:rgba(48,216,255,.28);background:rgba(48,216,255,.10)}
    .rt-pill--success{color:rgba(187,247,208,.96);border-color:rgba(34,197,94,.3);background:rgba(34,197,94,.12)}
    .rt-pill--muted{color:rgba(226,232,240,.88);border-color:rgba(148,163,184,.28);background:rgba(148,163,184,.10)}
    .rt-pill--warning{color:rgba(254,240,138,.96);border-color:rgba(250,204,21,.3);background:rgba(250,204,21,.12)}
    .rt-codeBtn{border:none;background:transparent;padding:0;font-size:.98rem;font-weight:800;color:rgba(48,216,255,.96);cursor:pointer;text-align:left}
    .rt-codeBtn:hover{color:rgba(120,227,255,.98)}
    .rt-capabilities,.rt-statusCell,.rt-usageStack,.rt-rowActions{display:flex;gap:8px;flex-wrap:wrap}
    .rt-emptyState{padding:28px;text-align:center;color:rgba(230,230,247,.72);line-height:1.6}
    .rt-guideTitle{margin:0;font-size:1rem;font-weight:800;color:rgba(230,230,247,.94)}
    .rt-guideBlock{margin-top:12px}
    .rt-guideLabel{font-size:.78rem;font-weight:800;color:rgba(48,216,255,.88);text-transform:uppercase;letter-spacing:.08em;margin-bottom:4px}
    .rt-field{display:flex;flex-direction:column}
    .rt-fieldLabel{display:block;margin-bottom:8px;font-size:.9rem;font-weight:800;color:rgba(230,230,247,.95)}
    .rt-fieldWarning{margin-top:8px;color:rgba(254,240,138,.9);font-size:.82rem;line-height:1.5}
    .rt-readOnly{border-radius:12px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);padding:10px 12px;min-height:44px;display:flex;align-items:center;color:rgba(230,230,247,.86)}
    .rt-formGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}
    .rt-field--full{grid-column:1/-1}
    .rt-checkHeader{display:flex;align-items:center;gap:10px;font-weight:800;color:rgba(230,230,247,.94);margin-bottom:8px}
    .rt-checkHeader input{width:18px;height:18px;accent-color:rgba(48,216,255,.95)}
    .rt-modalOverlay{position:fixed;inset:0;background:rgba(3,5,12,.72);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:20px;z-index:1100}
    .rt-modal{width:min(1080px,100%);max-height:calc(100vh - 40px);overflow:auto;border-radius:20px;border:1px solid transparent;background:linear-gradient(180deg,rgba(25,25,51,.98),rgba(10,10,22,.98)) padding-box,linear-gradient(135deg,rgba(48,216,255,.55),rgba(168,85,247,.65),rgba(255,77,79,.45)) border-box;box-shadow:0 32px 90px rgba(0,0,0,.6);color:rgba(230,230,247,.92)}
    .rt-modalHeader{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;padding:20px 20px 16px;border-bottom:1px solid rgba(255,255,255,.08)}
    .rt-modalTitle{margin:0;font-size:1.2rem;font-weight:800;color:rgba(230,230,247,.96)}
    .rt-modalSubtitle{margin-top:6px;font-size:.88rem;line-height:1.55;color:rgba(230,230,247,.68);max-width:760px}
    .rt-modalClose{width:38px;height:38px;border-radius:12px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.04);color:rgba(230,230,247,.86);font-size:1.5rem;cursor:pointer}
    .rt-modalBody{padding:20px;display:flex;flex-direction:column;gap:18px}
    .rt-modalActions{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap}
    @media (max-width:1280px){.rt-metricsGrid{grid-template-columns:repeat(3,minmax(0,1fr))}}
    @media (max-width:1160px){.rt-twoCol{grid-template-columns:1fr}}
    @media (max-width:900px){.rt-cardHeader{flex-direction:column;align-items:stretch}.rt-formGrid,.rt-guideGrid,.rt-checkGrid{grid-template-columns:1fr}}
    @media (max-width:768px){.rt-page{padding:0 12px 12px;height:100%}.rt-metricsGrid{grid-template-columns:repeat(2,minmax(0,1fr))}}
    @media (max-width:520px){.rt-metricsGrid{grid-template-columns:1fr}}
  `;

  return (
    <div className="rt-page">
      <style>{uiCss}</style>

      <div className="rt-stack">
        <section className="rt-card">
          <div className="rt-cardHeader">
            <div>
              <h1 className="rt-title">Review Topics Catalog</h1>
              <div className="rt-subtitle">
                Catálogo maestro de temas reputacionales definidos por producto. Aquí se decide qué
                topics existen en Reviews Intelligence, bajo qué grupo estratégico viven y con qué
                polaridad base deben interpretarse.
              </div>
            </div>
            <div className="rt-toolbar">
              <button type="button" className="rt-btn rt-btn--ghost" onClick={() => fetchTopics()}>
                Refresh
              </button>
              <button type="button" className="rt-btn rt-btn--primary" onClick={openCreateModal}>
                New topic
              </button>
            </div>
          </div>

          {feedback ? <div className="rt-banner rt-banner--success">{feedback}</div> : null}
          {loadError ? <div className="rt-banner rt-banner--error">{loadError}</div> : null}

          <div className="rt-body">
            <div className="rt-metricsGrid">
              <div className="rt-metricCard">
                <div className="rt-metricLabel">Total topics</div>
                <div className="rt-metricValue">{metrics.total}</div>
                <div className="rt-metricHelp">Número total de topics del catálogo maestro.</div>
              </div>
              <div className="rt-metricCard">
                <div className="rt-metricLabel">Active</div>
                <div className="rt-metricValue">{metrics.active}</div>
                <div className="rt-metricHelp">Topics disponibles para analítica actual.</div>
              </div>
              <div className="rt-metricCard">
                <div className="rt-metricLabel">Groups</div>
                <div className="rt-metricValue">{metrics.groups}</div>
                <div className="rt-metricHelp">Familias estratégicas hoy presentes en el catálogo.</div>
              </div>
              <div className="rt-metricCard">
                <div className="rt-metricLabel">Default negative</div>
                <div className="rt-metricValue">{metrics.negativeDefault}</div>
                <div className="rt-metricHelp">
                  Topics cuya lectura base suele representar fricción reputacional.
                </div>
              </div>
              <div className="rt-metricCard">
                <div className="rt-metricLabel">With history</div>
                <div className="rt-metricValue">{metrics.withHistory}</div>
                <div className="rt-metricHelp">
                  Topics con detecciones, action plans, constraints o agregados ya ligados.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rt-card">
          <div className="rt-cardHeader">
            <div>
              <h2 className="rt-title">Catalog maintenance</h2>
              <div className="rt-subtitle">
                Administra aquí la taxonomía base del módulo. La decisión crítica es que cada
                <code> code</code> sea estable, cada <code>topic_group</code> sea coherente y cada
                <code>polarity_default</code> refleje la lectura habitual del topic.
              </div>
            </div>
            <div className="rt-toolbar">
              <span className="rt-pill rt-pill--id">{metrics.filtered} visible</span>
              <span className="rt-pill rt-pill--warning">{metrics.withHistory} with history</span>
            </div>
          </div>

          <div className="rt-body">
            <div className="rt-filters">
              <div className="rt-filterGroup" style={{ flexBasis: "320px" }}>
                <label className="rt-filterLabel">Search</label>
                <input
                  className="rt-input"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by code, label, group or polarity..."
                />
              </div>

              <div className="rt-filterGroup">
                <label className="rt-filterLabel">Topic group</label>
                <select
                  className="rt-select"
                  value={groupFilter}
                  onChange={(event) => setGroupFilter(event.target.value)}
                >
                  <option value="all">All groups</option>
                  {topicGroupOptions.map((option) => (
                    <option key={option} value={option}>
                      {humanizeToken(option)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rt-filterGroup">
                <label className="rt-filterLabel">Default polarity</label>
                <select
                  className="rt-select"
                  value={polarityFilter}
                  onChange={(event) => setPolarityFilter(event.target.value)}
                >
                  <option value="all">All polarities</option>
                  {polarityOptions.map((option) => (
                    <option key={option} value={option}>
                      {humanizeToken(option)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rt-filterGroup">
                <label className="rt-filterLabel">Status</label>
                <select
                  className="rt-select"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">All statuses</option>
                  <option value="active">Only active</option>
                  <option value="inactive">Only inactive</option>
                </select>
              </div>
            </div>

            <div className="rt-tableWrap">
              <table className="rt-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Code & label</th>
                    <th>Group</th>
                    <th>Default polarity</th>
                    <th>Status</th>
                    <th>Historical usage</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7">
                        <div className="rt-emptyState">Loading review topics catalog...</div>
                      </td>
                    </tr>
                  ) : filteredTopics.length === 0 ? (
                    <tr>
                      <td colSpan="7">
                        <div className="rt-emptyState">
                          No hay topics que coincidan con los filtros actuales.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredTopics.map((topic) => (
                      <tr key={topic.id}>
                        <td>
                          <span className="rt-pill rt-pill--id">#{topic.id}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="rt-codeBtn"
                            onClick={() => openEditModal(topic)}
                          >
                            {topic.code}
                          </button>
                          <div className="rt-tableMeta">{topic.label}</div>
                          <div className="rt-tableMeta">{topic.description || "Sin descripción."}</div>
                        </td>
                        <td>
                          <span className="rt-pill">{humanizeToken(topic.topic_group)}</span>
                          <div className="rt-tableMeta">
                            Grupo estratégico usado para agregados y lectura táctica.
                          </div>
                        </td>
                        <td>
                          <span
                            className={`rt-pill ${
                              topic.polarity_default === "negative"
                                ? "rt-pill--warning"
                                : topic.polarity_default === "positive"
                                  ? "rt-pill--success"
                                  : "rt-pill--muted"
                            }`}
                          >
                            {topic.polarity_default_label || humanizeToken(topic.polarity_default)}
                          </span>
                          <div className="rt-tableMeta">
                            Referencia base del catálogo, no una obligación para cada review.
                          </div>
                        </td>
                        <td>
                          <div className="rt-statusCell">
                            <span
                              className={`rt-pill ${
                                topic.is_active ? "rt-pill--success" : "rt-pill--warning"
                              }`}
                            >
                              {topic.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="rt-tableMeta">Updated {formatTimestamp(topic.updated_at)}</div>
                        </td>
                        <td>
                          <div className="rt-usageStack">
                            <span className="rt-pill">
                              {topic.review_topics_count || 0} review links
                            </span>
                            <span className="rt-pill">
                              {topic.action_plans_count || 0} action plans
                            </span>
                            <span className="rt-pill">
                              {topic.operational_constraints_count || 0} constraints
                            </span>
                            <span className="rt-pill">
                              {topic.daily_aggregates_count || 0} aggregates
                            </span>
                            <span
                              className={`rt-pill ${
                                topic.can_be_deleted ? "rt-pill--success" : "rt-pill--warning"
                              }`}
                            >
                              {topic.can_be_deleted ? "Deletable" : "Keep for history"}
                            </span>
                          </div>
                          <div className="rt-tableMeta">Created {formatTimestamp(topic.created_at)}</div>
                        </td>
                        <td>
                          <div className="rt-rowActions">
                            <button
                              type="button"
                              className="rt-btn rt-btn--ghost"
                              onClick={() => openEditModal(topic)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="rt-btn rt-btn--danger"
                              onClick={() => openDeleteModal(topic)}
                              disabled={!topic.can_be_deleted}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <div className="rt-twoCol">
          <section className="rt-card">
            <div className="rt-cardHeader">
              <div>
                <h2 className="rt-title">How to feed this table correctly</h2>
                <div className="rt-subtitle">
                  Este catálogo no lo configura el hotelero final. Es taxonomía de producto y debe
                  mantenerse limpia, estable y accionable para que IA, trends y action plans no se
                  vuelvan caóticos.
                </div>
              </div>
            </div>
            <div className="rt-body">
              <div className="rt-helperItem">
                <strong>1. Crea topics que representen problemas o fortalezas reales.</strong> Deben
                ser categorías accionables, no frases literales de reviews.
              </div>
              <div className="rt-helperItem">
                <strong>2. Mantén el code estable.</strong> El topic puede cambiar de etiqueta o de
                descripción si mejora la claridad, pero el <code>code</code> debe seguir siendo la
                referencia técnica persistente.
              </div>
              <div className="rt-helperItem">
                <strong>3. Usa topic_group para agrupar estratégicamente.</strong> Ayuda a trends,
                filtros y agregados. No debe utilizarse para expresar urgencia.
              </div>
              <div className="rt-helperItem">
                <strong>4. Define polarity_default como referencia base.</strong> No obliga a que
                toda mención futura tenga la misma polaridad, pero orienta el modelo maestro.
              </div>
              <div className="rt-helperItem">
                <strong>5. Desactiva antes de borrar.</strong> Si ya existe historia analítica o
                planes asociados, la opción correcta es marcar <code>is_active = false</code>.
              </div>
            </div>
          </section>

          <section className="rt-card">
            <div className="rt-cardHeader">
              <div>
                <h2 className="rt-title">Recommended group semantics</h2>
                <div className="rt-subtitle">
                  Los grupos ayudan a leer la reputación por áreas de negocio, no solo por topics
                  sueltos.
                </div>
              </div>
            </div>
            <div className="rt-body">
              {topicGroupOptions.map((group) => (
                <div key={group} className="rt-helperItem">
                  <strong>{humanizeToken(group)}.</strong> Usa este grupo para topics cuya lectura
                  principal pertenezca a esa área estratégica del producto.
                </div>
              ))}
            </div>
          </section>
        </div>

        <section className="rt-card">
          <div className="rt-cardHeader">
            <div>
              <h2 className="rt-title">Field-by-field guide</h2>
              <div className="rt-subtitle">
                Explicación detallada de cada campo para mantener una taxonomía útil, auditable y
                estable en el tiempo.
              </div>
            </div>
          </div>
          <div className="rt-body">
            <div className="rt-guideGrid">
              {FIELD_GUIDE.map((field) => (
                <article key={field.key} className="rt-guideCard">
                  <h3 className="rt-guideTitle">{field.title}</h3>
                  <div className="rt-guideMeta">{field.storage}</div>
                  <div className="rt-guideBlock">
                    <div className="rt-guideLabel">Purpose</div>
                    <div className="rt-guideText">{field.purpose}</div>
                  </div>
                  <div className="rt-guideBlock">
                    <div className="rt-guideLabel">How to feed it</div>
                    <div className="rt-guideText">{field.howToFill}</div>
                  </div>
                  <div className="rt-guideBlock">
                    <div className="rt-guideLabel">What to avoid</div>
                    <div className="rt-guideText">{field.warning}</div>
                  </div>
                  <div className="rt-guideBlock">
                    <div className="rt-guideLabel">Concrete example</div>
                    <div className="rt-guideText">{field.example}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="rt-card">
          <div className="rt-cardHeader">
            <div>
              <h2 className="rt-title">Recommended starter examples</h2>
              <div className="rt-subtitle">
                Ejemplos orientativos para sembrar el catálogo inicial con cierta consistencia.
              </div>
            </div>
          </div>
          <div className="rt-body">
            <div className="rt-exampleWrap">
              <table className="rt-exampleTable">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Label</th>
                    <th>Group</th>
                    <th>Default polarity</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {EXAMPLE_TOPICS.map((topic) => (
                    <tr key={topic.code}>
                      <td>{topic.code}</td>
                      <td>{topic.label}</td>
                      <td>{humanizeToken(topic.topic_group)}</td>
                      <td>{humanizeToken(topic.polarity_default)}</td>
                      <td>{topic.is_active ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {showFormModal ? (
        <div className="rt-modalOverlay" onClick={closeFormModal}>
          <div className="rt-modal" onClick={(event) => event.stopPropagation()}>
            <div className="rt-modalHeader">
              <div>
                <h3 className="rt-modalTitle">
                  {isEditing ? "Edit review topic" : "Create review topic"}
                </h3>
                <div className="rt-modalSubtitle">
                  Define la taxonomía maestra que luego usarán la detección de topics, los trends y
                  los action plans. Cada decisión aquí afecta directamente a la estructura analítica
                  del módulo.
                </div>
              </div>
              <button type="button" className="rt-modalClose" onClick={closeFormModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="rt-modalBody">
                {formError ? <div className="rt-banner rt-banner--error">{formError}</div> : null}

                <div className="rt-formGrid">
                  <div className="rt-field">
                    <label className="rt-fieldLabel">ID</label>
                    <div className="rt-readOnly">
                      {formState.id ? `#${formState.id}` : "Se asignará automáticamente al guardar."}
                    </div>
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.id.howToFill}</div>
                    <div className="rt-fieldWarning">{FIELD_GUIDE_BY_KEY.id.warning}</div>
                  </div>

                  <div className="rt-field">
                    <label className="rt-fieldLabel">created_at / updated_at</label>
                    <div className="rt-readOnly">
                      {formState.created_at
                        ? `Created ${formatTimestamp(formState.created_at)} | Updated ${formatTimestamp(
                            formState.updated_at
                          )}`
                        : "Pilcore rellenará ambos timestamps automáticamente."}
                    </div>
                    <div className="rt-fieldHelp">
                      {FIELD_GUIDE_BY_KEY.created_updated_at.howToFill}
                    </div>
                    <div className="rt-fieldWarning">
                      {FIELD_GUIDE_BY_KEY.created_updated_at.warning}
                    </div>
                  </div>

                  <div className="rt-field">
                    <label className="rt-fieldLabel" htmlFor="review-topic-code">
                      code
                    </label>
                    <input
                      id="review-topic-code"
                      className="rt-input"
                      value={formState.code}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          code: normalizeCode(event.target.value),
                        }))
                      }
                      placeholder="cleanliness"
                      required
                    />
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.code.purpose}</div>
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.code.howToFill}</div>
                    <div className="rt-fieldWarning">{FIELD_GUIDE_BY_KEY.code.warning}</div>
                  </div>

                  <div className="rt-field">
                    <label className="rt-fieldLabel" htmlFor="review-topic-label">
                      label
                    </label>
                    <input
                      id="review-topic-label"
                      className="rt-input"
                      value={formState.label}
                      onChange={(event) =>
                        setFormState((current) => ({ ...current, label: event.target.value }))
                      }
                      placeholder="Cleanliness"
                      required
                    />
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.label.purpose}</div>
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.label.howToFill}</div>
                    <div className="rt-fieldWarning">{FIELD_GUIDE_BY_KEY.label.warning}</div>
                  </div>

                  <div className="rt-field rt-field--full">
                    <label className="rt-fieldLabel" htmlFor="review-topic-description">
                      description
                    </label>
                    <textarea
                      id="review-topic-description"
                      className="rt-textarea"
                      value={formState.description}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          description: event.target.value,
                        }))
                      }
                      rows={3}
                      placeholder="Describe el alcance real del topic y qué casos deberían caer aquí."
                    />
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.description.purpose}</div>
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.description.howToFill}</div>
                    <div className="rt-fieldWarning">{FIELD_GUIDE_BY_KEY.description.warning}</div>
                  </div>

                  <div className="rt-field">
                    <label className="rt-fieldLabel" htmlFor="review-topic-group">
                      topic_group
                    </label>
                    <input
                      id="review-topic-group"
                      className="rt-input"
                      value={formState.topic_group}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          topic_group: normalizeCode(event.target.value),
                        }))
                      }
                      list="review-topic-group-options"
                      placeholder="room"
                      required
                    />
                    <datalist id="review-topic-group-options">
                      {topicGroupOptions.map((option) => (
                        <option key={option} value={option} />
                      ))}
                    </datalist>
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.topic_group.purpose}</div>
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.topic_group.howToFill}</div>
                    <div className="rt-fieldWarning">{FIELD_GUIDE_BY_KEY.topic_group.warning}</div>
                  </div>

                  <div className="rt-field">
                    <label className="rt-fieldLabel" htmlFor="review-topic-polarity">
                      polarity_default
                    </label>
                    <select
                      id="review-topic-polarity"
                      className="rt-select"
                      value={formState.polarity_default || ""}
                      onChange={(event) =>
                        setFormState((current) => ({
                          ...current,
                          polarity_default: event.target.value,
                        }))
                      }
                    >
                      <option value="">Not set</option>
                      {polarityOptions.map((option) => (
                        <option key={option} value={option}>
                          {humanizeToken(option)}
                        </option>
                      ))}
                    </select>
                    <div className="rt-fieldHelp">{FIELD_GUIDE_BY_KEY.polarity_default.purpose}</div>
                    <div className="rt-fieldHelp">
                      {FIELD_GUIDE_BY_KEY.polarity_default.howToFill}
                    </div>
                    <div className="rt-fieldWarning">
                      {FIELD_GUIDE_BY_KEY.polarity_default.warning}
                    </div>
                  </div>
                </div>

                <div className="rt-checkGrid">
                  <div className="rt-checkCard">
                    <label className="rt-checkHeader">
                      <input
                        type="checkbox"
                        checked={Boolean(formState.is_active)}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            is_active: event.target.checked,
                          }))
                        }
                      />
                      <span>is_active</span>
                    </label>
                    <div className="rt-checkHelp">{FIELD_GUIDE_BY_KEY.is_active.purpose}</div>
                    <div className="rt-checkHelp">{FIELD_GUIDE_BY_KEY.is_active.howToFill}</div>
                    <div className="rt-checkHelp">{FIELD_GUIDE_BY_KEY.is_active.warning}</div>
                  </div>

                  <div className="rt-checkCard">
                    <label className="rt-checkHeader">
                      <span>Usage note</span>
                    </label>
                    <div className="rt-checkHelp">
                      {isEditing
                        ? `Review links: ${formState.review_topics_count || 0}. Action plans: ${
                            formState.action_plans_count || 0
                          }. Constraints: ${
                            formState.operational_constraints_count || 0
                          }. Aggregates: ${formState.daily_aggregates_count || 0}.`
                        : "Cuando este topic empiece a ser detectado o agregado, convendrá desactivarlo antes que borrarlo."}
                    </div>
                  </div>
                </div>

                <div className="rt-helperItem">
                  <strong>{isEditing ? "Historical usage." : "Recommended workflow."}</strong>{" "}
                  {isEditing
                    ? `${
                        formState.can_be_deleted
                          ? "Todavía puede borrarse porque no tiene relaciones históricas."
                          : "No debe borrarse; si deja de usarse, marca is_active = false."
                      }`
                    : "Define primero el catálogo maestro y después deja que reglas, IA y planes operativos se apoyen sobre estos topics estables."}
                </div>

                <div className="rt-modalActions">
                  <button type="button" className="rt-btn rt-btn--ghost" onClick={closeFormModal}>
                    Cancel
                  </button>
                  <button type="submit" className="rt-btn rt-btn--primary" disabled={saving}>
                    {saving ? "Saving..." : isEditing ? "Save changes" : "Create topic"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="rt-modalOverlay" onClick={closeDeleteModal}>
          <div
            className="rt-modal"
            style={{ width: "min(680px, 100%)" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rt-modalHeader">
              <div>
                <h3 className="rt-modalTitle">Delete review topic</h3>
                <div className="rt-modalSubtitle">
                  Solo debe borrarse un topic si nunca se ha usado. Si ya tiene historia analítica,
                  action plans o constraints asociados, la opción correcta es desactivarlo.
                </div>
              </div>
              <button type="button" className="rt-modalClose" onClick={closeDeleteModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleDelete}>
              <div className="rt-modalBody">
                {deleteError ? <div className="rt-banner rt-banner--error">{deleteError}</div> : null}
                <div className="rt-helperItem">
                  <strong>Topic selected:</strong> {deleteTarget.label} ({deleteTarget.code}).
                </div>
                <div className="rt-helperItem">
                  <strong>Historical usage:</strong> {deleteTarget.review_topics_count || 0} review
                  links, {deleteTarget.action_plans_count || 0} action plans,{" "}
                  {deleteTarget.operational_constraints_count || 0} constraints and{" "}
                  {deleteTarget.daily_aggregates_count || 0} aggregates.
                </div>
                <div className="rt-modalActions">
                  <button type="button" className="rt-btn rt-btn--ghost" onClick={closeDeleteModal}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rt-btn rt-btn--danger"
                    disabled={deleting || !deleteTarget.can_be_deleted}
                  >
                    {deleting ? "Deleting..." : "Delete topic"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ReviewTopicsCatalogAdmin;
